import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import {
  WorkflowActionType,
  WorkflowDto,
} from '../components/pages/canvas-pages/canvas-hubspot-page/types';
import { HubspotField } from '../components/pages/canvas-pages/canvas-hubspot-page/hubspot.types';
import {
  Campaign,
  CampaignFieldNamesForSfdcId,
  CampaignFields,
} from '../components/pages/canvas-pages/canvas-hubspot-page/campaign-dialog/campaignTypes';
import { HubspotEmail, HubspotForm, HubspotMembershipList, HubspotPage } from '@server/hubspot';
import { SFFieldInterface } from '../types/fields';

export interface HubspotState {
  hubspotOrgs: {
    data?: HubspotOrg[];
    isLoading: boolean;
    loaded: boolean;
    error?: unknown;
  };
  campaigns: {
    data?: Campaign[];
    isLoading: boolean;
    loaded: boolean;
    error?: unknown;
  };
  workflows: {
    data?: WorkflowDto[];
    isLoading: boolean;
    loaded: boolean;
    error?: unknown;
  };
  hubspotFields: {
    data: HubspotField[];
    isLoading: boolean;
    loaded: boolean;
    error?: unknown;
  };
  hubspotForms: {
    data: HubspotForm[];
    isLoading: boolean;
    loaded: boolean;
    error?: unknown;
  };
  hubspotPages: {
    data: HubspotPage[];
    isLoading: boolean;
    loaded: boolean;
    error?: unknown;
  };
  hubspotEmails: {
    data: HubspotEmail[];
    isLoading: boolean;
    loaded: boolean;
    error?: unknown;
  };
  hubspotMembershipLists: {
    data: HubspotMembershipList[];
    isLoading: boolean;
    loaded: boolean;
    error?: unknown;
  };
  campaignFieldNames: {
    data: CampaignFieldNamesForSfdcId[];
    isLoading: boolean;
    loaded: boolean;
    error?: unknown;
  };
  campaignFields: {
    data: CampaignFields[];
  };
  campaignObjectFields: {
    data: SFFieldInterface[];
    isLoading: boolean;
    loaded: boolean;
    error?: unknown;
  };
}

const initialState: HubspotState = {
  hubspotOrgs: { isLoading: false, loaded: false },
  campaigns: { isLoading: false, loaded: false },
  workflows: { isLoading: false, loaded: false },
  hubspotFields: { data: [], isLoading: false, loaded: false },
  hubspotForms: { data: [], isLoading: false, loaded: false },
  hubspotPages: { data: [], isLoading: false, loaded: false },
  hubspotEmails: { data: [], isLoading: false, loaded: false },
  hubspotMembershipLists: { data: [], isLoading: false, loaded: false },
  campaignFieldNames: { data: [], isLoading: false, loaded: false },
  campaignFields: { data: [] },
  campaignObjectFields: { data: [], isLoading: false, loaded: false },
};

export const hubspotSlice = createSlice({
  name: 'hubspot',
  initialState,
  reducers: {
    setHubspotOrgs: (state, action: PayloadAction<HubspotOrg[]>) => {
      state.hubspotOrgs.data = action.payload;
      state.hubspotOrgs.isLoading = false;
      state.hubspotOrgs.loaded = true;
    },
    addOrUpdateHubspotOrg: (state, action: PayloadAction<HubspotOrg>) => {
      const hsOrgToAdd = action.payload;
      if (state.hubspotOrgs.data) {
        const existingOrg = state.hubspotOrgs.data.find((hsOrg) => hsOrg.id === hsOrgToAdd.id);
        if (existingOrg) {
          state.hubspotOrgs.data = state.hubspotOrgs.data.map((hsOrg) => {
            if (hsOrg.id === existingOrg.id) {
              return hsOrgToAdd;
            } else {
              return hsOrg;
            }
          });
          return;
        }
        state.hubspotOrgs.data = [...state.hubspotOrgs.data, action.payload];
      } else {
        state.hubspotOrgs.data = [action.payload];
      }
    },
    deleteHubspotOrg: (state, action: PayloadAction<{ hsOrgId: string }>) => {
      state.hubspotOrgs.data = state.hubspotOrgs.data?.filter(
        (hsOrg) => hsOrg.id !== action.payload.hsOrgId,
      );
    },
    startLoadingCampaigns: (state) => {
      state.campaigns.isLoading = true;
    },
    loadCampaigns: (state, action: PayloadAction<Campaign[]>) => {
      state.campaigns.data = action.payload;
      state.campaigns.isLoading = false;
      state.campaigns.loaded = true;
    },
    setCampaignsLoadingError: (state, action: PayloadAction<unknown>) => {
      state.campaigns.error = action.payload;
      state.campaigns.isLoading = false;
    },
    addCampaign: (state, action: PayloadAction<Campaign>) => {
      if (state.campaigns.data) {
        state.campaigns.data = [...state.campaigns.data, action.payload];
      } else {
        state.campaigns.data = [action.payload];
      }
    },
    editCampaign: (state, action: PayloadAction<Campaign>) => {
      if (state.campaigns.data) {
        state.campaigns.data = state.campaigns.data.map((campaign) => {
          if (campaign.Id === action.payload.Id) {
            return action.payload;
          }
          return campaign;
        });
      }
    },
    removeCampaign: (state, action: PayloadAction<{ campaignId: string }>) => {
      if (state.campaigns.data) {
        state.campaigns.data = state.campaigns.data.filter(
          (campaign) => campaign.Id !== action.payload.campaignId,
        );
      }
    },
    startLoadingWorkflows: (state) => {
      state.workflows.isLoading = true;
    },
    loadWorkflows: (state, action: PayloadAction<WorkflowDto[]>) => {
      state.workflows.data = action.payload;
      state.workflows.isLoading = false;
      state.workflows.loaded = true;
    },
    removeWorkflow: (state, action: PayloadAction<{ workflowId: string }>) => {
      state.workflows.data = state.workflows.data?.filter(
        (workflow) => workflow.id !== action.payload.workflowId,
      );
    },
    addWorkflow: (state, action: PayloadAction<WorkflowDto>) => {
      if (state.workflows.data) {
        state.workflows.data = [...state.workflows.data, action.payload];
      } else {
        state.workflows.data = [action.payload];
      }
    },
    updateWorkflow: (state, action: PayloadAction<WorkflowDto>) => {
      if (!state.workflows.data) {
        state.workflows.data = [];
      }

      state.workflows.data = (state.workflows.data || []).map((workflow) => {
        if (workflow.id === action.payload.id) {
          return action.payload;
        }
        return workflow;
      });
    },
    startLoadingHubspotFields: (state) => {
      state.hubspotFields.isLoading = true;
    },
    setLoadingHubspotFieldsError: (state, action: PayloadAction<unknown>) => {
      state.hubspotFields.error = action.payload;
      state.hubspotFields.isLoading = false;
    },
    loadHubspotFields: (state, action: PayloadAction<HubspotField[]>) => {
      state.hubspotFields.data = action.payload;
      state.hubspotFields.isLoading = false;
      state.hubspotFields.loaded = true;
    },
    startLoadingHubspotPages: (state) => {
      state.hubspotPages.isLoading = true;
    },
    setLoadingHubspotPagesError: (state, action: PayloadAction<unknown>) => {
      state.hubspotPages.error = action.payload;
      state.hubspotPages.isLoading = false;
    },
    loadHubspotPages: (state, action: PayloadAction<HubspotPage[]>) => {
      state.hubspotPages.data = action.payload;
      state.hubspotPages.isLoading = false;
    },
    startLoadingHubspotForms: (state) => {
      state.hubspotForms.isLoading = true;
    },
    loadHubspotForms: (state, action: PayloadAction<HubspotForm[]>) => {
      state.hubspotForms.data = action.payload;
      state.hubspotForms.isLoading = false;
      state.hubspotForms.loaded = true;
    },
    setLoadingHubspotFormsError: (state, action: PayloadAction<unknown>) => {
      state.hubspotForms.error = action.payload;
      state.hubspotForms.isLoading = false;
    },
    startLoadingHubspotEmails: (state) => {
      state.hubspotEmails.isLoading = true;
    },
    loadHubspotEmails: (state, action: PayloadAction<HubspotEmail[]>) => {
      state.hubspotEmails.data = action.payload.map((email) => ({
        emailId: email.name,
        name: email.name,
      }));
      state.hubspotEmails.isLoading = false;
      state.hubspotEmails.loaded = true;
    },
    setLoadingHubspotEmailsError: (state, action: PayloadAction<unknown>) => {
      state.hubspotEmails.error = action.payload;
      state.hubspotForms.isLoading = false;
    },
    startLoadingHubspotMembershipLists: (state) => {
      state.hubspotMembershipLists.isLoading = true;
    },
    loadHubspotMembershipLists: (state, action: PayloadAction<HubspotMembershipList[]>) => {
      state.hubspotMembershipLists.data = action.payload;
      state.hubspotMembershipLists.isLoading = false;
      state.hubspotMembershipLists.loaded = true;
    },
    setLoadingHubspotMembershipListsError: (state, action: PayloadAction<unknown>) => {
      state.hubspotMembershipLists.error = action.payload;
      state.hubspotMembershipLists.isLoading = false;
    },
    startLoadingCampaignFieldNames: (state) => {
      state.campaignFieldNames.isLoading = true;
    },
    loadCampaignFieldNames: (state, action: PayloadAction<CampaignFieldNamesForSfdcId[]>) => {
      state.campaignFieldNames.data = action.payload;
      state.campaignFieldNames.isLoading = false;
      state.campaignFieldNames.loaded = true;
    },
    setLoadingCampaignFieldNamesError: (state, action: PayloadAction<unknown>) => {
      state.campaignFieldNames.error = action.payload;
      state.campaignFieldNames.isLoading = false;
    },
    loadAllCampaignFields: (state, action: PayloadAction<CampaignFields[]>) => {
      state.campaignFields.data = action.payload;
    },
    loadCampaignObjectFields: (state, action: PayloadAction<SFFieldInterface[]>) => {
      state.campaignObjectFields.data = action.payload;
      state.campaignObjectFields.isLoading = false;
      state.campaignObjectFields.loaded = true;
    },
    startLoadingCampaignObjectFields: (state) => {
      state.campaignObjectFields.isLoading = true;
    },
    setLoadedCampaignObjectFieldsError: (state, action: PayloadAction<unknown>) => {
      state.campaignObjectFields.error = action.payload;
      state.campaignObjectFields.isLoading = false;
    },
  },
});

export const {
  setHubspotOrgs,
  addOrUpdateHubspotOrg,
  addCampaign,
  editCampaign,
  removeCampaign,
  loadCampaigns,
  setCampaignsLoadingError,
  startLoadingCampaigns,
  deleteHubspotOrg,
  startLoadingWorkflows,
  loadWorkflows,
  addWorkflow,
  updateWorkflow,
  removeWorkflow,
  startLoadingHubspotFields,
  loadHubspotFields,
  startLoadingHubspotPages,
  loadHubspotPages,
  startLoadingHubspotForms,
  loadHubspotForms,
  setLoadingHubspotFieldsError,
  setLoadingHubspotPagesError,
  setLoadingHubspotFormsError,
  startLoadingHubspotEmails,
  loadHubspotEmails,
  setLoadingHubspotEmailsError,
  loadHubspotMembershipLists,
  startLoadingHubspotMembershipLists,
  setLoadingHubspotMembershipListsError,
  loadCampaignFieldNames,
  setLoadingCampaignFieldNamesError,
  startLoadingCampaignFieldNames,
  loadAllCampaignFields,
  loadCampaignObjectFields,
  startLoadingCampaignObjectFields,
  setLoadedCampaignObjectFieldsError,
} = hubspotSlice.actions;

export const selectHubspotOrgs = (state: RootState) => state.hubspot.hubspotOrgs.data;
export const selectConnectedHubspotOrg = (state: RootState) =>
  state.hubspot.hubspotOrgs.data?.find((hsOrg) => hsOrg.isConnected);
export const selectHubspotOrgsIsLoaded = (state: RootState) => state.hubspot.hubspotOrgs.loaded;
export const selectIsLoadingHsOrgs = (state: RootState) => state.hubspot.hubspotOrgs.isLoading;
export const selectIsLoadedHsOrgs = (state: RootState) => state.hubspot.hubspotOrgs.loaded;

export const selectCampaigns = (state: RootState) => state.hubspot.campaigns.data;
export const selectCampaignsIsLoading = (state: RootState) => state.hubspot.campaigns.isLoading;
export const selectCampaignsError = (state: RootState) => state.hubspot.campaigns.error;
export const selectCampaignsIsLoaded = (state: RootState) => state.hubspot.campaigns.loaded;
export const selectCampaignById = (campaignId: string) => (state: RootState) =>
  state.hubspot.campaigns.data?.find((campaign) => campaign.Id === campaignId);

export const selectWorkflows = (state: RootState) => state.hubspot.workflows.data;
export const selectWorkflowsIsLoading = (state: RootState) => state.hubspot.workflows.isLoading;
export const selectWorkflowsError = (state: RootState) => state.hubspot.workflows.error;
export const selectWorkflowsIsLoaded = (state: RootState) => state.hubspot.workflows.loaded;
export const selectWorkflowById = (workflowId?: string) => (state: RootState) =>
  state.hubspot.workflows.data?.find((workflow) => workflow.hubspotWorkflowId === workflowId);

export const selectCampaignWorkflows = (campaignId: string) => (state: RootState) =>
  state.hubspot.workflows.data?.filter((workflow) =>
    workflow.workflowDetails.actions.some(
      (action) =>
        action.actionType === WorkflowActionType.ENROLL_CAMPAIGN &&
        action.campaignId === campaignId,
    ),
  ) || [];

export const selectHubspotFields = (state: RootState) => state.hubspot.hubspotFields.data;
export const selectHubspotFieldsIsLoading = (state: RootState) =>
  state.hubspot.hubspotFields.isLoading;
export const selectHubspotFieldsError = (state: RootState) => state.hubspot.hubspotFields.error;
export const selectHubspotFieldsIsLoaded = (state: RootState) => state.hubspot.hubspotFields.loaded;
export const selectHubspotFieldByName = (name: string) => (state: RootState) =>
  state.hubspot.hubspotFields.data.find((field) => field.name === name);

export const selectHubspotPagesData = (state: RootState) => state.hubspot.hubspotPages.data;
export const selectHubspotFormsData = (state: RootState) => state.hubspot.hubspotForms.data;
export const selectHubspotEmailsData = (state: RootState) => state.hubspot.hubspotEmails.data;
export const selectHubspotEmailsIsLoading = (state: RootState) =>
  state.hubspot.hubspotEmails.isLoading;
export const selectHubspotEmailsError = (state: RootState) => state.hubspot.hubspotEmails.error;
export const selectHubspotEmailsIsLoaded = (state: RootState) => state.hubspot.hubspotEmails.loaded;

export const selectHubspotMembershipListsData = (state: RootState) =>
  state.hubspot.hubspotMembershipLists.data;
export const selectHubspotMembershipListsIsLoading = (state: RootState) =>
  state.hubspot.hubspotMembershipLists.isLoading;
export const selectHubspotMembershipListsError = (state: RootState) =>
  state.hubspot.hubspotMembershipLists.error;
export const selectHubspotMembershipListsIsLoaded = (state: RootState) =>
  state.hubspot.hubspotMembershipLists.loaded;

export const selectCampaignObjectFieldsData = (state: RootState) =>
  state.hubspot.campaignObjectFields.data;
export const selectCampaignObjectFieldsIsLoading = (state: RootState) =>
  state.hubspot.campaignObjectFields.isLoading;
export const selectCampaignObjectFieldsError = (state: RootState) =>
  state.hubspot.campaignObjectFields.error;
export const selectCampaignObjectFieldsIsLoaded = (state: RootState) =>
  state.hubspot.campaignObjectFields.loaded;

export const selectAllCampaignFields = (state: RootState) => state.hubspot.campaignFields.data;

export const selectCampaignFieldNamesData = (state: RootState) =>
  state.hubspot.campaignFieldNames.data;
export const selectCampaignFieldNamesIsLoading = (state: RootState) =>
  state.hubspot.campaignFieldNames.isLoading;
export const selectCampaignFieldNamesError = (state: RootState) =>
  state.hubspot.campaignFieldNames.error;
export const selectCampaignFieldNamesIsLoaded = (state: RootState) =>
  state.hubspot.campaignFieldNames.loaded;

export default hubspotSlice.reducer;
